/**
 * Renderizado seguro de plantillas de correo (§13.4, RNF-05): todas las variables se escapan,
 * el HTML usa estilos en línea, tabla de 600 px y cabecera de marca #465DA9 como `MailSend`.
 */
import { PlantillaCorreoEntity } from '../models/alertas.model';

export function escaparHtml(valor: string): string {
  return valor
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export interface RenderOpciones {
  baseUrl: string;
  ruta: string;
  rutaPreferencias: string;
  obligatoria: boolean;
  motivoRecepcion: string;
}

export interface CorreoRenderizado {
  asunto: string;
  html: string;
  texto: string;
  mensajePanel: string;
  faltantes: string[];
}

function sustituir(texto: string, vars: Record<string, string>, faltantes: Set<string>, escapar: boolean): string {
  return texto.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, nombre: string) => {
    const v = vars[nombre];
    if (v === undefined || v === null) {
      faltantes.add(nombre);
      return '';
    }
    return escapar ? escaparHtml(String(v)) : String(v);
  });
}

function urlSegura(baseUrl: string, ruta: string): string {
  const limpia = ruta.startsWith('/') ? ruta : `/${ruta}`;
  return escaparHtml(`${baseUrl.replace(/\/$/, '')}${limpia}`);
}

export function renderizarPlantilla(p: PlantillaCorreoEntity, vars: Record<string, string>, op: RenderOpciones): CorreoRenderizado {
  const faltantes = new Set<string>();
  const asunto = sustituir(p.asunto, vars, faltantes, false).replace(/\s+/g, ' ').trim();
  const parrafosTexto = p.cuerpo.split(/\n\s*\n/).map(x => sustituir(x, vars, faltantes, false).trim()).filter(Boolean);
  const parrafosHtml = p.cuerpo.split(/\n\s*\n/).map(x => sustituir(x, vars, new Set(), true).trim()).filter(Boolean);
  const url = urlSegura(op.baseUrl, op.ruta);
  const logo = urlSegura(op.baseUrl, '/assets/images/logo/logo-med-joven.svg');
  const pref = urlSegura(op.baseUrl, op.rutaPreferencias);

  const html = `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width"><title>${escaparHtml(asunto)}</title></head>
<body style="margin:0;padding:0;background:#f3f4f6;font-family:Arial,Helvetica,sans-serif;color:#1f2937;">
<span style="display:none;max-height:0;overflow:hidden;">${escaparHtml(sustituir(p.preheader, vars, new Set(), false))}</span>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f3f4f6;padding:24px 0;"><tr><td align="center">
<table role="presentation" width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:#ffffff;border-radius:12px;overflow:hidden;">
<tr><td style="background:#465DA9;padding:20px 24px;"><img src="${logo}" alt="Medellín Joven" height="36" style="display:block;height:36px;"></td></tr>
<tr><td style="padding:16px 24px 8px;">${parrafosHtml.map(x => `<p style="margin:0 0 14px;font-size:15px;line-height:1.55;">${x}</p>`).join('')}</td></tr>
<tr><td style="padding:0 24px 24px;"><a href="${url}" style="display:inline-block;background:#465DA9;color:#ffffff;text-decoration:none;padding:12px 20px;border-radius:8px;font-weight:bold;font-size:14px;">${escaparHtml(p.accionEtiqueta)}</a></td></tr>
<tr><td style="padding:16px 24px;background:#f9fafb;font-size:12px;color:#6b7280;line-height:1.5;">Por qué recibes este correo: ${escaparHtml(op.motivoRecepcion)}.${op.obligatoria ? ' Es una notificación obligatoria del proceso formativo.' : ` Puedes ajustar tus <a href="${pref}" style="color:#465DA9;">preferencias de notificación</a>.`}<br>Music Lab · Medellín Joven</td></tr>
</table></td></tr></table></body></html>`;

  const texto = `${parrafosTexto.join('\n\n')}\n\n${p.accionEtiqueta}: ${op.baseUrl.replace(/\/$/, '')}${op.ruta}\n\n--\nPor qué recibes este correo: ${op.motivoRecepcion}.`;
  const mensajePanel = parrafosTexto.slice(1).join(' ').slice(0, 320);
  return { asunto, html, texto, mensajePanel, faltantes: [...faltantes] };
}
